import React, { useCallback, useState, memo } from 'react';
import './CodePreviewPanel.css';

export interface CodePreviewPanelProps {
  /** iframe 加载的 Blob URL */
  readonly previewUrl: string | null;
  /** 是否正在加载文件 */
  readonly loading?: boolean;
  /** 关闭预览 */
  readonly onClose: () => void;
  /** 刷新预览 */
  readonly onRefresh: () => void;
}

/** 设备模式 */
type DeviceMode = 'desktop' | 'tablet' | 'mobile';

interface DeviceConfig {
  readonly label: string;
  readonly icon: string;
  readonly width: string;
}

const DEVICE_CONFIGS: Readonly<Record<DeviceMode, DeviceConfig>> = {
  desktop: { label: '桌面', icon: 'fa-desktop', width: '100%' },
  tablet: { label: '平板', icon: 'fa-tablet-alt', width: '768px' },
  mobile: { label: '手机', icon: 'fa-mobile-alt', width: '375px' },
};

const ALL_DEVICES: readonly DeviceMode[] = ['desktop', 'tablet', 'mobile'];

/**
 * 代码预览面板
 * 使用 iframe + Blob URL 渲染 HTML/CSS/JS
 */
export const CodePreviewPanel: React.FC<CodePreviewPanelProps> = memo(({
  previewUrl,
  loading = false,
  onClose,
  onRefresh,
}) => {
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('desktop');

  const handleDeviceChange = useCallback((mode: DeviceMode): void => {
    setDeviceMode(mode);
  }, []);

  const handleOpenExternal = useCallback((): void => {
    if (previewUrl) {
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    }
  }, [previewUrl]);

  const currentDevice = DEVICE_CONFIGS[deviceMode];

  return (
    <div className="cpv-panel">
      {/* 工具栏 */}
      <div className="cpv-toolbar">
        <div className="cpv-toolbar-left">
          <span className="cpv-title">
            <i className="fas fa-eye" />
            <span>预览</span>
          </span>
          {loading && (
            <span className="cpv-loading-badge">
              <i className="fas fa-spinner fa-spin" />
              <span>加载中</span>
            </span>
          )}
        </div>

        <div className="cpv-toolbar-center">
          {ALL_DEVICES.map(mode => (
            <button
              key={mode}
              type="button"
              className={`cpv-device-btn ${deviceMode === mode ? 'active' : ''}`}
              onClick={() => handleDeviceChange(mode)}
              title={DEVICE_CONFIGS[mode].label}
            >
              <i className={`fas ${DEVICE_CONFIGS[mode].icon}`} />
            </button>
          ))}
        </div>

        <div className="cpv-toolbar-right">
          <button
            type="button"
            className="cpv-action-btn"
            onClick={onRefresh}
            title="刷新预览"
          >
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`} />
          </button>
          <button
            type="button"
            className="cpv-action-btn"
            onClick={handleOpenExternal}
            title="在新窗口打开"
          >
            <i className="fas fa-external-link-alt" />
          </button>
          <button
            type="button"
            className="cpv-action-btn cpv-close-btn"
            onClick={onClose}
            title="关闭预览"
          >
            <i className="fas fa-times" />
          </button>
        </div>
      </div>

      {/* iframe 容器 */}
      <div className="cpv-body">
        {previewUrl ? (
          <div
            className="cpv-iframe-container"
            style={{ maxWidth: currentDevice.width }}
          >
            <iframe
              className="cpv-iframe"
              src={previewUrl}
              sandbox="allow-scripts allow-same-origin"
              title="代码预览"
            />
          </div>
        ) : (
          <div className="cpv-empty">
            <i className="fas fa-code" />
            <p>{loading ? '正在加载预览...' : '暂无可预览的内容'}</p>
          </div>
        )}
      </div>
    </div>
  );
});

CodePreviewPanel.displayName = 'CodePreviewPanel';